/**
 * Shared constants for map components (StadiaMap and StadiaPlaygroundMap)
 * This file contains common configuration values used across different map implementations
 */
export const MAP_CONSTANTS = {
  /** Default maximum zoom level for maps */
  DEFAULT_MAX_ZOOM: 20,
  /** Default map bounds (worldwide) */
  DEFAULT_MAP_BOUNDS: [
    { lat: 90.0, lng: 90.0 },
    { lat: -90.0, lng: -90.0 }
  ] as const,
  /** Europe-specific bounds for default view */
  EUROPE_BOUNDS: {
    topLeft: { latitude: 65, longitude: -12 },
    bottomRight: { latitude: 15, longitude: 35 }
  } as const,
  /** Padding around map bounds when fitting to markers */
  MAP_BOUNDS_PADDING: 50,
  /** Mobile-specific padding */
  MOBILE_PADDING: 50,
  /** Desktop-specific padding */
  DESKTOP_PADDING: 80,
  /** Review step bottom padding for mobile (accounts for trip carousel) */
  REVIEW_MOBILE_BOTTOM_PADDING: 200,
  /** Minimum number of markers required for curved routes */
  MIN_MARKERS_FOR_CURVE: 2,
  /** Resolution for bezier curve generation */
  CURVE_RESOLUTION: 5000,
  /** Sharpness factor for bezier curves (0-1) */
  CURVE_SHARPNESS: 0.5,
  /** Animation duration for map transitions */
  MAP_ANIMATION_DURATION: 1500,
  /** Fly-to animation duration */
  FLY_TO_DURATION: 500,
  /** Expansion zoom added to cluster expansion */
  EXPANSION_ZOOM_ADDED: 2,
  /** Cluster radius for supercluster */
  CLUSTER_RADIUS: 40,
  /** Initial view state for maps */
  INITIAL_VIEW_STATE: {
    latitude: 20,
    longitude: 0,
    zoom: 1
  } as const,
  /** Line styles for route rendering */
  LINE_STYLES: {
    /** Main route line */
    MAIN: {
      type: 'line' as const,
      paint: {
        'line-width': 2,
        'line-color': '#000000'
      }
    },
    /** Background/halo line for better visibility */
    HALO: {
      paint: {
        'line-width': 8,
        'line-color': '#ffffff'
      }
    }
  },
  /** Arrow symbol configuration for route direction */
  ARROW_SYMBOL: {
    'symbol-placement': 'line',
    'symbol-spacing': 100,
    'symbol-avoid-edges': true,
    'icon-image': 'triangle_11',
    'icon-size': 1.1,
    'icon-allow-overlap': false,
    'icon-ignore-placement': false,
    'icon-rotation-alignment': 'map',
    'icon-text-fit': 'none',
    'icon-rotate': 90
  },
  /** Arrow symbol configuration for trip routes (smaller spacing) */
  TRIP_ARROW_SYMBOL: {
    'symbol-placement': 'line',
    'symbol-spacing': 75,
    'symbol-avoid-edges': true,
    'icon-image': 'triangle_11',
    'icon-size': 1.1,
    'icon-allow-overlap': false,
    'icon-ignore-placement': false,
    'icon-rotation-alignment': 'map',
    'icon-text-fit': 'none',
    'icon-rotate': 90
  },
  /** Collision circle configuration */
  COLLISION_CIRCLE: {
    'icon-image': 'circle_11',
    'icon-size': 0.8,
    'icon-allow-overlap': true,
    'icon-ignore-placement': false
  },
  /** Z-index configuration for markers */
  MARKER_Z_INDEX: {
    UNSELECTABLE: 0,
    STACKED_LABEL: 1,
    BASE: 2,
    CLUSTER: 3,
    SELECTED: 4,
    START_END: 5,
    /** Z-index for AI sparkle place markers — must always sit above all other marker types */
    SPARKLE: 6,
    FOCUSED: 7,
    POPUP: 10
  },
  /** Transition configuration for expanded map */
  EXPANDED_MAP_TRANSITION: {
    ENTER: 'transition-opacity duration-300',
    ENTER_FROM: 'opacity-0',
    ENTER_TO: 'opacity-100',
    LEAVE: 'transition-opacity duration-300',
    LEAVE_FROM: 'opacity-100',
    LEAVE_TO: 'opacity-0'
  },
  /** CSS classes for expanded map */
  EXPANDED_MAP_CLASSES: {
    OVERLAY:
      'fixed inset-0 z-50 flex items-center justify-center w-screen h-screen p-6 overflow-hidden bg-white cursor-[zoom-out] lg:p-12',
    CONTAINER: 'overflow-hidden rounded aspect-[4/3] w-full max-w-4xl h-full',
    EXPAND_BUTTON:
      'hidden absolute bottom-0 left-0 p-2 mb-6 ml-4 text-white bg-black bg-opacity-60 rounded cursor-pointer md:block'
  },
  /** Map configuration flags */
  MAP_CONFIG: {
    /** Whether to remove origin from default bounds calculation */
    REMOVE_ORIGIN_FROM_DEFAULT_BOUNDS: false,
    /** Whether to enable drag rotation */
    DRAG_ROTATE: false,
    /** Whether to reuse maps. Disabled to avoid stale review trip layers leaking into build view. */
    REUSE_MAPS: false,
    /** Whether to show attribution control */
    SHOW_ATTRIBUTION: false,
    /** Whether to show compass in navigation control */
    SHOW_COMPASS: false,
    /** Whether to show zoom in navigation control */
    SHOW_ZOOM: true
  },
  /** Mobile breakpoint */
  MOBILE_BREAKPOINT: 768,
  /** Attribution control positioning */
  ATTRIBUTION_POSITION: {
    MOBILE: '0px',
    DESKTOP: '5px'
  }
} as const;

/**
 * Type for map bounds using full property names
 */
export type MapBounds = {
  topLeft: { latitude: number; longitude: number };
  bottomRight: { latitude: number; longitude: number };
};
